/**
 * «Розклад в інверторі зараз» (D2): the inverter's TOU program as last read
 * from DeyeCloud — the switch, the work mode when DeyeCloud reports it, the
 * 6 windows (time, charge from the grid, power, SOC) — with «Оновити з
 * інвертора», at most once in 10 minutes (DeyeCloud limits sign-ins). The
 * windows table is that of the day's windows: cards on a phone.
 */
import type { SiteResponse } from 'shared';

import { useLocale } from '../i18n/locale-context.js';
import { RefreshInverterSettings } from './RefreshInverterSettings.js';
import { fmtDateTime, fmtNumShort } from './format.js';
import { programRows, workModeText } from './inverter-program.js';

export function InverterProgram({ site, canRefresh }: { site: SiteResponse; canRefresh: boolean }) {
  const { t, tf, locale } = useLocale();
  const tz = site.timezone;
  const s = site.inverter_settings;
  const p = s.program;

  return (
    <div className="inv-prog">
      <div className="chart-title">{t('site.prog.title')}</div>
      <p className="sub" style={{ margin: '0 0 10px' }}>
        {s.read_at ? tf('site.prog.read', { at: fmtDateTime(s.read_at, tz) }) : t('site.prog.never')}
      </p>
      {p ? (
        <>
          <dl className="kv2">
            <dt>{t('site.orig.tou')}</dt>
            <dd>{t(p.enabled ? 'site.orig.tou.on' : 'site.orig.tou.off')}</dd>
            {p.work_mode ? (
              <>
                <dt>{t('site.orig.mode')}</dt>
                <dd>{workModeText(t, p.work_mode)}</dd>
              </>
            ) : null}
          </dl>
          <div className="table-wrap">
            <table className="wins ro">
              <thead>
                <tr>
                  <th>{t('site.win.col.window')}</th>
                  <th>{t('site.win.col.time')}</th>
                  <th>{t('site.prog.col.grid')}</th>
                  <th>{t('site.win.col.power')}</th>
                  <th>{t('site.win.col.soc')}</th>
                </tr>
              </thead>
              <tbody>
                {programRows(p).map((r) => (
                  <tr key={r.n}>
                    <td>
                      <span className={`idx idx-${r.gridCharge ? 'grid_charge' : 'self_consumption'}`}>
                        {r.n}
                      </span>
                    </td>
                    <td>
                      <span className="num">{r.from}</span> – <span className="num">{r.to}</span>
                    </td>
                    <td>
                      <span className="cell-label">{t('site.prog.col.grid')}</span>
                      {t(r.gridCharge ? 'site.prog.yes' : 'site.prog.no')}
                    </td>
                    <td>
                      <span className="cell-label">{t('site.win.col.power')}</span>
                      <span className="num">{fmtNumShort(locale, r.powerKw, 1)}</span>
                    </td>
                    <td>
                      <span className="cell-label">{t('site.win.col.soc')}</span>
                      <span className="num">{r.socPct === null ? '' : fmtNumShort(locale, r.socPct, 0)}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : s.read_at ? (
        <p className="sub">{t('site.prog.empty')}</p>
      ) : null}
      {canRefresh ? <RefreshInverterSettings site={site} /> : null}
    </div>
  );
}
